import React, { useRef, useState } from "react"
import {
  Box, Input, Popover, PopoverAnchor, PopoverArrow, PopoverBody,
  PopoverContent, PopoverTrigger, useDisclosure,
} from "@chakra-ui/react"
import SegmentColorPicker from "./SegmentColorPicker"

type Props = {
  initialColor?: string
  // Parent owns the API call; throw to surface an error toast, return on
  // success to let the popover close.
  onSave: (name: string, color: string) => Promise<void>
  // Click mode (default): children act as the popover trigger.
  // Controlled mode (when `controlledOpen` is set): children are just the
  // positioning anchor — opening / closing is driven by the parent so the
  // popover can be summoned from outside (e.g. a menu item in a split
  // button without altering the button's own click handler).
  controlledOpen?: boolean
  onControlledClose?: () => void
  children: React.ReactElement
}

const SaveSegmentPopover = ({
  initialColor = "blue", onSave, controlledOpen, onControlledClose, children,
}: Props) => {
  const disclosure = useDisclosure()
  const isControlled = controlledOpen !== undefined
  const isOpen = isControlled ? controlledOpen : disclosure.isOpen

  const [name, setName] = useState("")
  const [color, setColor] = useState(initialColor)
  const [submitting, setSubmitting] = useState(false)
  // Chakra's PopoverContent autoFocus chooses the close button by default —
  // pass `initialFocusRef` so the name input gets focus on open instead.
  const inputRef = useRef<HTMLInputElement>(null)

  const reset = () => {
    setName("")
    setColor(initialColor)
    setSubmitting(false)
    if (isControlled) onControlledClose?.()
    else disclosure.onClose()
  }

  const handleOpen = () => {
    setName("")
    setColor(initialColor)
    if (!isControlled) disclosure.onOpen()
  }

  const submit = async () => {
    if (!name.trim() || submitting) return
    setSubmitting(true)
    try {
      await onSave(name.trim(), color)
      reset()
    } catch {
      setSubmitting(false)
    }
  }

  return (
    <Popover
      isOpen={isOpen}
      onOpen={handleOpen}
      onClose={reset}
      placement="bottom-end"
      isLazy
      initialFocusRef={inputRef}
    >
      {isControlled
        ? <PopoverAnchor>{children}</PopoverAnchor>
        : <PopoverTrigger>{children}</PopoverTrigger>}
      <PopoverContent w="380px" overflow="hidden">
        <PopoverArrow />
        <PopoverBody p={0} overflow="hidden" borderRadius="md">
          <Box p={4}>
            {/* Flushed input mirrors the SegmentForm TextField look — no
                separate label, the border-bottom carries the affordance. */}
            <Input
              ref={inputRef}
              variant="flushed"
              placeholder="Segment name"
              value={name}
              onChange={(e) => setName(e.target.value)}
              onKeyDown={(e) => { if (e.key === "Enter") submit() }}
            />
            <Box mt={4}>
              <SegmentColorPicker value={color} onChange={setColor} />
            </Box>
          </Box>
          {/* Same shape + interaction states as the AddFilterPopover
              Apply so both popovers feel like one family. */}
          <Box
            as="button"
            type="button"
            w="full"
            py={2}
            borderTopWidth={1}
            borderColor="gray.200"
            color="blue.500"
            fontWeight="semibold"
            cursor={(!name.trim() || submitting) ? "not-allowed" : "pointer"}
            opacity={!name.trim() ? 0.4 : 1}
            transition="background-color 0.2s ease"
            disabled={!name.trim() || submitting}
            onClick={submit}
            sx={(!name.trim() || submitting) ? undefined : {
              "&:hover": { bg: "blue.50" },
              "&:active": { bg: "blue.100" },
            }}
          >
            {submitting ? "Saving…" : "Save"}
          </Box>
        </PopoverBody>
      </PopoverContent>
    </Popover>
  )
}

export default SaveSegmentPopover
